import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
  TextField,
  MenuItem,
  Button,
  Typography,
  Switch,
  FormControlLabel,
  InputAdornment,
  Paper,
} from "@mui/material";
import { useCustomTranslation } from "../../../hooks/useCustomTranslation";
import { useApiClientMutation } from "../../../hooks/useApiClient";
import api_endpoints from "../../../api";

export default function AddPaymentPlanModal({
  organizationId,
  open,
  onClose,
  onCreated,
}) {
  const { t } = useCustomTranslation("Recurring Payment");

  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [frequency, setFrequency] = useState("Monthly");
  const [taxable, setTaxable] = useState(false);
  const [desc, setDesc] = useState("");
  const [billingAnchorDay, setBillingAnchorDay] = useState("");
  const [firstChargeStrategy, setFirstChargeStrategy] = useState("anchor_only");

  const canSubmit = useMemo(() => {
    const n = Number(amount);
    return name.trim().length > 0 && Number.isFinite(n) && n > 0 && !!organizationId;
  }, [name, amount, organizationId]);

  const createPlanMutation = useApiClientMutation(
    (body) => api_endpoints.createRecurringPlan(body),
    [["recurring-plans", organizationId]],
    (data) => {
      onCreated?.(data);
      onClose?.();
    }
  );

  function handleSubmit() {
    if (!canSubmit || createPlanMutation.isPending) return;
    createPlanMutation.mutate({
      organization_id: organizationId,
      name: name.trim(),
      amount: Number(amount),
      frequency,
      description: desc.trim() || undefined,
      taxable,
      billing_anchor_day: frequency === "Monthly" && billingAnchorDay ? Number(billingAnchorDay) : null,
      first_charge_strategy: frequency === "Monthly" && billingAnchorDay ? firstChargeStrategy : null,
    });
  }

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>{t("add_payment_plan_modal")}</DialogTitle>

      <DialogContent dividers sx={{ pt: 1.5 }}>
          <Typography
            variant="subtitle2"
            align="center"
            sx={{ mb: 2, textTransform: "uppercase", letterSpacing: 0.6 }}
          >
            {t("payment_plan_creation")}
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label={t("plan_name")}
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                sx={{ ml: 0, height: "100%" }}
                control={
                  <Switch
                    checked={taxable}
                    onChange={(e) => setTaxable(e.target.checked)}
                  />
                }
                label={t("taxable")}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label={t("amount")}
                type="number"
                fullWidth
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                inputProps={{ min: 0, step: "0.01" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label={t("frequency")}
                value={frequency}
                onChange={(e) => setFrequency(e.target.value)}
              >
                <MenuItem value="Weekly">{t("weekly")}</MenuItem>
                <MenuItem value="Monthly">{t("monthly")}</MenuItem>
                <MenuItem value="Quarterly">{t("quarterly")}</MenuItem>
                <MenuItem value="Yearly">{t("yearly")}</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label={t("description")}
                fullWidth
                multiline
                minRows={2}
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
              />
            </Grid>
          </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} color="error">
          {t("cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit || createPlanMutation.isPending}
        >
          {t("add")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
